/**
 * Format date utilities
 */

// Map app language codes to Intl locale strings
const localeMap = {
  en: 'en-US',
  vi: 'vi-VN',
  pl: 'pl-PL',
};

const resolveLocale = (lang) => localeMap[lang] || lang || 'en-US';

/**
 * Format date to readable string
 * @param {string|Date} date - Date to format
 * @param {string} locale - Language code or locale (e.g. 'en', 'vi', 'pl', 'en-US')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, locale = 'en-US') => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return dateObj.toLocaleDateString(resolveLocale(locale), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Format date to short string
 * @param {string|Date} date - Date to format
 * @param {string} locale - Language code or locale (e.g. 'en', 'vi', 'pl', 'en-US')
 * @returns {string} Formatted date string
 */
export const formatDateShort = (date, locale = 'en-US') => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return dateObj.toLocaleDateString(resolveLocale(locale), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Get relative time string (e.g., "2 days ago")
 * @param {string|Date} date - Date to format
 * @returns {string} Relative time string
 */
export const getRelativeTime = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now - dateObj) / 1000);
  
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  
  return formatDate(dateObj);
};

export default {
  formatDate,
  formatDateShort,
  getRelativeTime,
};
